import { useState } from "react";

import { filterConstants } from "../../constants";
import {
  useQuickSubmitFormHelper,
  useGetPageQueryParams,
  useMerchantApiQueryWithParams,
  useQueryString,
  useReactRouterLink,
} from "../../hooks";
import {
  IQueryStringNavLinks,
  ITableHeader,
  IViewBankAccount,
  IPageAndSearchRequest,
  PagedMeta,
  IViewBankAccountTag,
} from "../../interfaces-and-types";
import { View } from "./View";
import { QuickSubmitForm } from "../../components";

interface IBankAccountQueryMeta {
  is_disabled: boolean;
}

export interface IBankAccountViewProps {
  data: IViewBankAccount[];
  headers: ITableHeader[];
  isLoading: boolean;
  isError: boolean;
  totalCount: number;
  queryStringTabs: IQueryStringNavLinks;

  defaultCurrency: string | null;

  // modals
  isCreateNewModalOpen: boolean;
  closeModal: () => void;
  refreshTable: () => void;

  // extra buttons
  onExport: () => void;
  openCreateNewModal: () => void;
}

export const BankAccounts = () => {
  // const queryClient = useQueryClient();
  const query = useQueryString();
  const is_disabled = query.get(filterConstants.isDisabledKey);
  const { page, page_size, search } = useGetPageQueryParams();
  const { location, navigate } = useReactRouterLink();

  const requestParams = {
    page: (page && Number(page)) || 1,
    page_size: (page_size && Number(page_size)) || 10,
    search,
    is_disabled: Boolean(is_disabled || false),
  };

  const {
    isLoading: isLoadingBankAccounts,
    isError: isErrorBankAccounts,
    data: bankAccounts,
    meta: bankAccountsMeta,
    refetch: refetchBankAccounts,
  } = useMerchantApiQueryWithParams<
    IPageAndSearchRequest & IBankAccountQueryMeta,
    IViewBankAccount[],
    PagedMeta
  >(["getBankAccounts", requestParams]);

  // const { balances } = useBalances();
  // const defaultCurrency = balances.find(bal => bal.is_local_currency_balance)

  const defaultCurrencyBankAcct = bankAccounts?.find(
    (b) => b.is_primary_account_number
  );

  const [isCreateNewModalOpen, setCreateNewModalOpen] = useState(false);
  const closeModal = () => {
    setCreateNewModalOpen(false);
  };

  const headers: ITableHeader[] = [
    {
      key: "account_name",
      header: "Account Name",
    },
    {
      key: "account_number",
      header: "Account Number",
    },
    {
      key: "country_name",
      header: "Country",
    },
    {
      key: "currency",
      header: "Currency",
    },
    {
      key: "bank_name",
      header: "Bank Name",
    },
    {
      key: "settles_to",
      header: "Settles To",
    },
    {
      key: "payout_tags",
      header: "Payout Tags",
    },
    // {
    //   key: "status",
    //   header: "Status",
    // },
    {
      key: "more",
      header: "",
    },
  ];

  const queryStringTabs: IQueryStringNavLinks = {
    query: filterConstants.isDisabledKey,
    options: [
      {
        key: "Active",
        value: false,
      },
      // {
      //   key: "Disabled",
      //   value: true,
      // },
    ],
  };

  const onExport = () => {
    alert("exporting data...");
  };

  const openCreateNewModal = () => {
    setCreateNewModalOpen(true);
  };
  // const openCreateNewModal = () => {
  //   navigate(`create?${query}`);
  // };

  const refreshTable = () => refetchBankAccounts();

  // const refreshTable = () => {
  //   queryClient.invalidateQueries(["getBankAccounts", requestParams]);
  // };

  // insert the functions to open edit/delete/make_primary modals
  const bankAccountsWithLinks: IViewBankAccount[] | undefined =
    bankAccounts?.map((bankAcc) => ({
      ...bankAcc,
      openDeleteModal: () =>
        openDeleteModal(bankAcc.id, bankAcc.is_primary_account_number),
      setAsPrimaryAccount: () =>
        openSetAsPrimaryBankAccountModal(
          bankAcc.id,
          bankAcc.is_primary_account_number
        ),
      toggleSettleToWalletOrBank: () =>
        bankAcc.settles_to_wallet
          ? openToggleSettleToBankModal(bankAcc.id, true)
          : openToggleSettleToWalletModal(bankAcc.id, true),
      openDeleteAccountTagModal: (tagId: string) =>
        openDeleteBankAccountTagModal(tagId, false),
      openAppendAccountTagModal: (bankAccountId: string) =>
        navigate(`${bankAccountId}/tags/append`),
      closeAppendAccountTagModal: (bankAccountId: string) =>
        navigate(location.pathname.split(`${bankAccountId}/tags/append`)[0]),
    }));

  const {
    isModalOpen: isDeleteModalOpen,
    openModal: openDeleteModal,
    closeModal: closeDeleteModal,

    isItemDeleteLocked,

    isLoading: isLoadingDeleteItem,
    isSuccess: isSuccessDeleteItem,
    mutate: deleteItem,
  } = useQuickSubmitFormHelper("deleteBankAccount", refreshTable);

  // NOTE: transfer the more complex actions to a details page not <QuickForm />
  const {
    isModalOpen: isSetAsPrimaryBankAccountModalOpen,
    openModal: openSetAsPrimaryBankAccountModal,
    closeModal: closeSetAsPrimaryBankAccountModal,

    isItemDeleteLocked: isBankAccountEditLocked,

    isLoading: isLoadingSetAsPrimaryBankAccount,
    isSuccess: isSuccessSetAsPrimaryBankAccount,
    mutate: setAsPrimaryBankAccount,
  } = useQuickSubmitFormHelper("setAsPrimaryAccount", refreshTable, "PUT");

  const {
    isModalOpen: isToggleSettleToWalletModalOpen,
    openModal: openToggleSettleToWalletModal,
    closeModal: closeToggleSettleToWalletModal,

    isItemDeleteLocked: isToggleSettleToWalletEditLocked,

    isLoading: isLoadingToggleSettleToWallet,
    isSuccess: isSuccessToggleSettleToWallet,
    mutate: toggleSettleToWallet,
  } = useQuickSubmitFormHelper("settleToWallet", refreshTable, "PUT");

  const {
    isModalOpen: isToggleSettleToBankModalOpen,
    openModal: openToggleSettleToBankModal,
    closeModal: closeToggleSettleToBankModal,

    isItemDeleteLocked: isToggleSettleToBankEditLocked,

    isLoading: isLoadingToggleSettleToBank,
    isSuccess: isSuccessToggleSettleToBank,
    mutate: toggleSettleToBank,
  } = useQuickSubmitFormHelper("settleToBank", refreshTable, "PUT");

  const {
    isModalOpen: isDeleteBankAccountTagModalOpen,
    openModal: openDeleteBankAccountTagModal,
    closeModal: closeDeleteBankAccountTagModal,

    // isItemDeleteLocked: isBankAccountTagDeleteLocked,

    isLoading: isLoadingDeleteBankAccountTag,
    isSuccess: isSuccessDeleteBankAccountTag,
    mutate: deleteBankAccountTagById,
  } = useQuickSubmitFormHelper("deleteBankAccountTag", refreshTable);

  return (
    <>
      <View
        defaultCurrency={defaultCurrencyBankAcct?.currency || null}
        data={bankAccountsWithLinks || []}
        headers={headers}
        isLoading={isLoadingBankAccounts}
        isError={isErrorBankAccounts}
        totalCount={bankAccountsMeta?.page_meta.item_count || 0}
        queryStringTabs={queryStringTabs}
        onExport={onExport}
        openCreateNewModal={openCreateNewModal}
        isCreateNewModalOpen={isCreateNewModalOpen}
        closeModal={closeModal}
        refreshTable={refreshTable}
      />

      <QuickSubmitForm
        isModalOpen={isDeleteModalOpen}
        closeModal={closeDeleteModal}
        formTitle="Remove Bank Account"
        isDeleteLocked={isItemDeleteLocked}
        isSubmitting={isLoadingDeleteItem}
        isSuccess={isSuccessDeleteItem}
        onButtonClick={deleteItem}
        confirmationMessage={
          <>
            <h4>Are you sure you want to delete this account?</h4>
            <br />
            <span className="text-danger">
              (NOTE: Any products tied to it will now payout into the primary
              bank account)
            </span>
          </>
        }
        deleteLockedInfo={
          <>
            <h4 className="text-danger">
              Primary account numbers cannot be deleted.
            </h4>
            <br />
            <p>Please set a different account as primary before deleting!</p>
          </>
        }
      />

      <QuickSubmitForm
        formTitle="Change Primary Account Number"
        isModalOpen={isSetAsPrimaryBankAccountModalOpen}
        closeModal={closeSetAsPrimaryBankAccountModal}
        isDeleteLocked={isBankAccountEditLocked}
        isSubmitting={isLoadingSetAsPrimaryBankAccount}
        isSuccess={isSuccessSetAsPrimaryBankAccount}
        buttonText={"Set As Primary"}
        onButtonClick={setAsPrimaryBankAccount}
        confirmationMessage={
          <>
            <h4>
              Are you sure you want to set this account number as primary?
            </h4>
            <br />

            <span className="text-danger">
              (NOTE: You can set still a different bank account as primary
              later.)
            </span>
          </>
        }
      />

      <QuickSubmitForm
        formTitle="Change Settlement Location To Wallet"
        isModalOpen={isToggleSettleToWalletModalOpen}
        closeModal={closeToggleSettleToWalletModal}
        isDeleteLocked={isBankAccountEditLocked}
        isSubmitting={isLoadingToggleSettleToWallet}
        isSuccess={isSuccessToggleSettleToWallet}
        buttonText={"Settle to Wallet"}
        buttonKind="success"
        onButtonClick={toggleSettleToWallet}
        confirmationMessage={
          <>
            <h4>
              Are you sure you want to set this bank account to settle to
              wallet?
            </h4>
            <br />

            <span className="text-danger">
              (NOTE: You can change this later.)
            </span>
          </>
        }
      />

      <QuickSubmitForm
        formTitle="Change Settlement Location To Bank"
        isModalOpen={isToggleSettleToBankModalOpen}
        closeModal={closeToggleSettleToBankModal}
        isDeleteLocked={isBankAccountEditLocked}
        isSubmitting={isLoadingToggleSettleToBank}
        isSuccess={isSuccessToggleSettleToBank}
        buttonText={"Settle to Bank"}
        buttonKind="primary"
        onButtonClick={toggleSettleToBank}
        confirmationMessage={
          <>
            <h4>
              Are you sure you want to set this bank account to settle to bank?
            </h4>
            <br />

            <span className="text-danger">
              (NOTE: You can change this later.)
            </span>
          </>
        }
      />

      <QuickSubmitForm
        isModalOpen={isDeleteBankAccountTagModalOpen}
        closeModal={closeDeleteBankAccountTagModal}
        formTitle="Remove Bank Account Tag"
        isDeleteLocked={false}
        isSubmitting={isLoadingDeleteBankAccountTag}
        isSuccess={isSuccessDeleteBankAccountTag}
        onButtonClick={deleteBankAccountTagById}
        confirmationMessage={
          <>
            <h4>Are you sure you want to remove this account tag?</h4>
            <br />
            <span className="text-danger">
              (NOTE: Any payouts tied to it will now payout into the primary
              bank account)
            </span>
          </>
        }
      />
    </>
  );
};
